<template>
  <LoadingDiv v-if="!isReady" />

  <div v-else class="scheme-manager">
    <header class="setup-manager-header">
      <div>
        <span>Coverage configuration</span>
        <h1>Schemes</h1>
        <p>Manage private and social schemes available to policies and members.</p>
      </div>
      <button type="button" class="setup-outline-button" @click="handleNew">
        <X v-if="addNew" :size="16" /><Plus v-else :size="16" />
        {{ addNew ? "Cancel" : "Add scheme" }}
      </button>
    </header>

    <div v-if="success" class="setup-success"><CheckCircle2 :size="16" /> New scheme added.</div>

    <form v-if="addNew" class="setup-form-card" @submit.prevent="handleSubmit">
      <div class="setup-form-heading">
        <h2>Add scheme</h2>
        <p>Create a social scheme and define its payment settings.</p>
      </div>
      <div v-if="error" class="setup-error">{{ error }}</div>
      <div class="setup-field-grid">
        <label class="setup-field">
          <span>Scheme name</span>
          <input type="text" v-model="newScheme" required />
        </label>
        <label class="setup-field">
          <span>Who pays</span>
          <select v-model="payer" required>
            <option value="" disabled>Select payer</option>
            <option value="hmo">HMO pays</option>
            <option value="gov">Government pays</option>
          </select>
        </label>
        <label class="setup-field setup-field-wide">
          <span>Scheme type</span>
          <input type="text" value="Social scheme" readonly />
        </label>
        <label v-if="showSchemeControls" class="setup-checkbox">
          <input type="checkbox" v-model="isManual" />
          <span>Allow provider manual batch start</span>
        </label>
        <label v-if="showSchemeControls" class="setup-field">
          <span>Professional fee percentage</span>
          <input type="number" min="0" max="100" step="0.01" v-model="professionalFeePercentage" />
        </label>
      </div>
      <footer class="setup-form-footer">
        <button class="setup-primary-button" :disabled="isSubmitting">
          <span v-if="isSubmitting" class="setup-button-spinner"></span><Plus v-else :size="16" />
          {{ isSubmitting ? "Adding..." : "Add scheme" }}
        </button>
      </footer>
    </form>

    <section class="scheme-list" v-if="schemes.length">
      <article v-for="(scheme, index) in schemes" :key="scheme.id" class="scheme-card">
        <template v-if="scheme.id !== isEditing">
          <div class="scheme-index">{{ index + 1 }}</div>
          <div class="scheme-copy">
            <div class="scheme-title-row">
              <h2>{{ scheme.name }}</h2>
              <span class="setup-badge">{{ scheme.type === "p" ? "Private" : "Social" }}</span>
            </div>
            <div class="scheme-meta">
              <span class="setup-badge">{{ scheme.who_pays.toUpperCase() }} pays</span>
              <span v-if="showSchemeControls" class="setup-badge">{{ isManualScheme(scheme) ? "Manual batch enabled" : "Manual batch disabled" }}</span>
              <span v-if="showSchemeControls" class="setup-badge">Professional fee: {{ scheme.professional_fee_percentage || 0 }}%</span>
            </div>
            <p>Created {{ new Date(scheme.created_at).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" }) }}</p>
            <div v-if="error && errorId === scheme.id" class="setup-error">{{ error }}</div>
          </div>
          <button type="button" class="setup-icon-button" title="Edit scheme" @click="handleEdit(scheme)"><Pencil :size="16" /></button>
        </template>

        <form v-else class="scheme-edit-form" @submit.prevent="handleEditConfirm(scheme)">
          <div class="setup-form-heading">
            <h2>Edit scheme</h2>
            <p>{{ scheme.type === "p" ? "Private scheme" : "Social scheme" }}</p>
          </div>
          <div class="setup-field-grid">
            <label class="setup-field">
              <span>Name</span>
              <input type="text" v-model="name" required />
            </label>
            <label v-if="showSchemeControls" class="setup-checkbox">
              <input type="checkbox" v-model="editIsManual" />
              <span>Manual batch</span>
            </label>
            <label v-if="showSchemeControls" class="setup-field">
              <span>Professional fee percentage</span>
              <input type="number" min="0" max="100" step="0.01" v-model="editProfessionalFeePercentage" />
            </label>
          </div>
          <div class="scheme-edit-actions">
            <button type="button" class="setup-outline-button" @click="handleCancel"><X :size="16" /> Cancel</button>
            <button class="setup-primary-button" :disabled="isSubmitting"><CheckCircle2 :size="16" /> Update</button>
          </div>
        </form>
      </article>
    </section>

    <div v-else class="setup-empty-state">No schemes have been added.</div>

    <footer class="setup-page-actions">
      <button type="button" class="setup-primary-button" @click="handleFinish">Continue <ArrowRight :size="16" /></button>
    </footer>
  </div>
</template>
<script>
import { computed, onMounted, ref } from "vue";
import { ArrowRight, CheckCircle2, Pencil, Plus, X } from "@lucide/vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";


export default {
  name: "SchemeSetupComponent",
  components: { ArrowRight, CheckCircle2, LoadingDiv, Pencil, Plus, X },
  props: {
    showSchemeControls: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["next"],
  setup(props, context) {
    // start
    const response = ref(null);
    const error = ref(null);
    const errorId = ref(null);
    const addNew = ref(null);
    const success = ref(false);
    const newScheme = ref(false);
    const payer = ref(null);
    const isReady = ref(false);
    const scheme = ref(null);
    const isEditing = ref(null);
    const isSubmitting = ref(false);
    const name = ref(null);
    const isManual = ref(false);
    const professionalFeePercentage = ref(0);
    const editIsManual = ref(false);
    const editProfessionalFeePercentage = ref(0);

    // start
    onMounted(() => {
      // get items
      (async () => {
        const { res, err } = await getItems("scheme");

        if (res.value) {
          response.value = res.value.data;

          // page is ready
          isReady.value = true;
        } else {
          error.value = err.value;
        }
      })();
    });

    // handle new scheme
    const handleNew = () => {
      addNew.value = !addNew.value;
      newScheme.value = null;
      success.value = false;
    };

    // handle submit
    const handleSubmit = async () => {
      isSubmitting.value = true;

      // update backend (post item)
      let val = {
        name: newScheme.value,
        who_pays: payer.value,
        type: "s",
      };

      if (props.showSchemeControls) {
        val.is_manual = isManual.value;
        val.professional_fee_percentage = Number(professionalFeePercentage.value) || 0;
      }

      const { res, err } = await postItem("scheme", val);

      if (res.value) {
        // update local array (backend is sorted)
        response.value.push(res.value.data);
        // success
        success.value = true;
      } else {
        error.value = err.value;
      }

      handleCancel();
    };

    // handle edit
    const handleEdit = (scheme) => {
      isEditing.value = scheme?.id;
      name.value = scheme?.name;
      if (props.showSchemeControls) {
        editIsManual.value = isManualScheme(scheme);
        editProfessionalFeePercentage.value = Number(scheme?.professional_fee_percentage) || 0;
      }
    };

    // handle cancel
    const handleCancel = () => {
      isSubmitting.value = false;
      isEditing.value = null;
      name.value = null;
      newScheme.value = null;
      payer.value = null;
      error.value = null;
      addNew.value = false;
      isManual.value = false;
      professionalFeePercentage.value = 0;
      editIsManual.value = false;
      editProfessionalFeePercentage.value = 0;
    };

    // handle edit confirm
    const handleEditConfirm = (scheme) => {
      isSubmitting.value = true;
      response.value.forEach(async (x) => {
        if (x.id == scheme?.id) {
          // keep track of errorId (if required)
          errorId.value = x.id;

          // update backend (post item)
          let val = {
            name: name.value,
          };

          if (props.showSchemeControls) {
            val.is_manual = editIsManual.value;
            val.professional_fee_percentage = Number(editProfessionalFeePercentage.value) || 0;
          }

          const { res, err } = await putItem("scheme", x.id, val);

          if (res.value) {
            // update local array (backend is sorted)
            x.name = res.value.data.name;
            if (props.showSchemeControls) {
              x.is_manual = res.value.data.is_manual;
              x.professional_fee_percentage = res.value.data.professional_fee_percentage;
            }
          } else {
            error.value = err.value;
          }
        }
      });
      handleCancel();
    };

    // handle finish
    const handleFinish = () => {
      // start
      handleCancel();
      success.value = false;

      // next
      // emit new parameters
      context.emit("next", {
        step: 2,
        isComplete: true,
      });

    };

    // schemes
    const schemes = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const isManualScheme = (scheme) => {
      return scheme?.is_manual === true || scheme?.is_manual === 1 || scheme?.is_manual === "1";
    };


    return {
      handleSubmit,
      handleEdit,
      handleEditConfirm,
      handleCancel,
      handleNew,
      handleFinish,
      isReady,
      schemes,
      scheme,
      error,
      errorId,
      addNew,
      success,
      payer,
      newScheme,
      isEditing,
      isSubmitting,
      name,
      isManual,
      professionalFeePercentage,
      editIsManual,
      editProfessionalFeePercentage,
      isManualScheme,
    };
  },
};
</script>

<style scoped>
.scheme-manager { display: grid; gap: 1rem; }
.setup-manager-header, .setup-form-card, .scheme-card, .setup-empty-state { background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); box-shadow: var(--ui-shadow-xs); color: var(--ui-card-foreground); }
.setup-manager-header { align-items: center; display: flex; gap: 1rem; justify-content: space-between; padding: 1.5rem; }
.setup-manager-header > div > span { color: var(--ui-primary); font-size: .7rem; font-weight: 700; text-transform: uppercase; }
.setup-manager-header h1, .setup-form-heading h2, .scheme-copy h2 { font-family: var(--ui-font-display); font-weight: 700; letter-spacing: -.02em; margin: .25rem 0 0; }
.setup-manager-header h1 { font-size: 1.35rem; }
.setup-manager-header p, .setup-form-heading p, .scheme-copy p { color: var(--ui-muted-foreground); margin: .35rem 0 0; }
.setup-outline-button, .setup-primary-button, .setup-icon-button { align-items: center; border-radius: calc(var(--ui-radius) - 2px); display: inline-flex; font: inherit; font-size: .8rem; font-weight: 600; gap: .45rem; justify-content: center; min-height: 2.5rem; padding: 0 .85rem; }
.setup-outline-button, .setup-icon-button { background: var(--ui-background); border: 1px solid var(--ui-border); color: var(--ui-foreground); }
.setup-primary-button { background: var(--ui-primary); border: 1px solid var(--ui-primary); color: var(--ui-primary-foreground); }
.setup-icon-button { height: 2.5rem; padding: 0; width: 2.5rem; }
.setup-success { align-items: center; background: color-mix(in srgb, var(--ui-success) 8%, var(--ui-background)); border: 1px solid color-mix(in srgb, var(--ui-success) 24%, var(--ui-border)); border-radius: var(--ui-radius); color: var(--ui-success); display: flex; font-size: .78rem; gap: .45rem; padding: .7rem .85rem; }
.setup-form-card { padding: 1.5rem; }
.setup-form-heading { margin-bottom: 1rem; }
.setup-form-heading h2, .scheme-copy h2 { font-size: 1rem; }
.setup-form-heading p, .scheme-copy p { font-size: .78rem; }
.setup-field-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.setup-field { display: grid; gap: .4rem; }
.setup-field > span { font-size: .75rem; font-weight: 600; }
.setup-field :is(input, select) { background: var(--ui-background); border: 1px solid var(--ui-input); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); font: inherit; height: 2.6rem; padding: 0 .7rem; width: 100%; }
.setup-field input:read-only { background: var(--ui-muted); color: var(--ui-muted-foreground); }
.setup-field-wide { grid-column: 1 / -1; }
.setup-checkbox { align-items: center; display: flex; font-size: .78rem; gap: .55rem; min-height: 2.6rem; }
.setup-checkbox input { opacity: 1; pointer-events: auto; position: static; }
.setup-form-footer, .setup-page-actions, .scheme-edit-actions { display: flex; gap: .6rem; justify-content: flex-end; margin-top: 1.25rem; }
.setup-error { background: color-mix(in srgb, var(--ui-destructive) 8%, var(--ui-background)); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-destructive); font-size: .76rem; margin-bottom: .75rem; padding: .65rem .75rem; }
.scheme-list { display: grid; gap: .75rem; }
.scheme-card { align-items: flex-start; display: grid; gap: 1rem; grid-template-columns: 2rem minmax(0, 1fr) auto; padding: 1.1rem; }
.scheme-index { align-items: center; background: var(--ui-secondary); border-radius: 999px; display: flex; font-size: .7rem; font-weight: 700; height: 2rem; justify-content: center; width: 2rem; }
.scheme-title-row, .scheme-meta { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; }
.setup-badge { background: var(--ui-secondary); border: 1px solid var(--ui-border); border-radius: 999px; color: var(--ui-secondary-foreground); font-size: .68rem; font-weight: 600; padding: .25rem .5rem; }
.scheme-meta { margin-top: .7rem; }
.scheme-edit-form { grid-column: 1 / -1; }
.setup-empty-state { color: var(--ui-muted-foreground); padding: 2rem; text-align: center; }
.setup-button-spinner { animation: setup-spin .7s linear infinite; border: 2px solid rgb(255 255 255 / .35); border-radius: 50%; border-top-color: currentColor; height: 1rem; width: 1rem; }
@keyframes setup-spin { to { transform: rotate(360deg); } }
@media (max-width: 700px) {
  .setup-manager-header { align-items: stretch; flex-direction: column; }
  .setup-field-grid { grid-template-columns: 1fr; }
  .setup-field-wide { grid-column: auto; }
  .scheme-card { grid-template-columns: 2rem minmax(0, 1fr); }
  .scheme-card > .setup-icon-button { grid-column: 2; }
  .setup-page-actions .setup-primary-button { width: 100%; }
}
</style>
